'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import SiteNav from '@/components/SiteNav';
import LandingFooter from '@/app/landing-page/components/LandingFooter';
import { ArrowRight } from 'lucide-react';

interface BlogPost {
  slug: string;
  title: string;
  description: string;
  date: string;
  level: string;
  schemaType: string;
  subheading: string;
}

const posts: BlogPost[] = [
  {
    slug: 'ask-anna-which-ai-tool-should-i-start-with',
    title: 'Ask Anna: Which AI Tool Should I Start With?',
    description:
      "Which AI tool should I start with? Anna's honest recommendation — which tool fits which situation, and why starting with all of them is a mistake.",
    date: 'September 2026',
    level: 'L6 Ask Anna',
    schemaType: 'FAQPage',
    subheading: "There are five worth knowing. Here's the one to start with — and why.",
  },
  {
    slug: 'the-week-ai-finally-clicked-for-me',
    title: 'The Week AI Finally Clicked for Me',
    description:
      'The week AI finally clicked for Anna — what the moment actually felt like, why it took longer than expected, and what it means for you.',
    date: 'September 2026',
    level: 'L5 Anna Notes',
    schemaType: 'Article',
    subheading: "It wasn't a revelation. That's the part worth knowing.",
  },
  {
    slug: 'ask-anna-should-i-pay-for-chatgpt',
    title: 'Ask Anna: Should I Pay for ChatGPT?',
    description:
      "Should I pay for ChatGPT? Anna's honest answer — when the free version is enough, when the upgrade is worth it, and what to consider first.",
    date: 'September 2026',
    level: 'L6 Ask Anna',
    schemaType: 'FAQPage',
    subheading: 'The honest answer — including the cases where the free version is fine.',
  },
  {
    slug: 'nobody-told-me-learning-ai-would-feel-like-this',
    title: 'Nobody Told Me Learning AI Would Feel Like This',
    description:
      "What does learning AI actually feel like? The emotional experience nobody talks about — from the unexpected personal quality of early failures to the quiet surprise of the first win.",
    date: 'August 2026',
    level: 'L5 Anna Notes',
    schemaType: 'Article',
    subheading: "The part they leave out of every beginner's guide.",
  },
  {
    slug: 'will-ai-replace-experienced-workers',
    title: 'Will AI Replace Experienced Workers — or Help Them?',
    description:
      "Will AI replace experienced workers? The honest answer — what's actually at risk, what isn't, and what experienced professionals should do about it.",
    date: 'August 2026',
    level: 'L2 Questions',
    schemaType: 'FAQPage',
    subheading: "The honest answer is neither — and both. Here's what the evidence actually shows.",
  },
  {
    slug: 'what-is-hallucination-in-ai',
    title: 'What Is Hallucination in AI — and Should I Worry About It?',
    description:
      "What is hallucination in AI? It's when AI produces confident but incorrect information. Here's what it looks like, why it happens, and how to manage it.",
    date: 'August 2026',
    level: 'L4 Definitions',
    schemaType: 'DefinedTerm',
    subheading: 'The word sounds alarming. The reality is more mundane — and more manageable.',
  },
  {
    slug: 'using-ai-for-career-reinvention-after-50',
    title: 'Using AI for Career Reinvention After 50',
    description:
      'How to use AI to think clearly about career reinvention after 50 — understanding your value, exploring options honestly, and preparing for the conversations that matter.',
    date: 'August 2026',
    level: 'L3 Practical',
    schemaType: 'HowTo',
    subheading: "You don't need a new career. You might just need a clearer picture of the one you already have.",
  },
  {
    slug: 'what-is-generative-ai',
    title: 'What Is Generative AI — in Plain English?',
    description:
      "What is generative AI in plain English? It's the type of AI that creates new content — text, images, code. Here's what that means and why it matters.",
    date: 'August 2026',
    level: 'L4 Definitions',
    schemaType: 'DefinedTerm',
    subheading: "The term that's everywhere, explained in the time it takes to make a cup of tea.",
  },
  {
    slug: 'the-quiet-way-ai-helps-managers',
    title: 'The Quiet Way AI Is Helping Managers Think More Clearly',
    description:
      'How managers are using AI to prepare for difficult conversations, tough presentations, and challenging meetings. Plain English. No hype.',
    date: 'July 2026',
    level: 'L3 Practical',
    schemaType: 'HowTo',
    subheading: 'Not productivity hacks. Something more useful: better thinking before the difficult conversations.',
  },
  {
    slug: 'can-i-learn-ai-if-im-not-a-tech-person',
    title: "Can I Learn AI If I'm Not a Tech Person?",
    description:
      "Can I learn AI if I'm not a tech person? Yes — here's why technical skill isn't actually required, and what skill matters instead.",
    date: 'July 2026',
    level: 'L2 Questions',
    schemaType: 'FAQPage',
    subheading: "You don't need to be technical. You need to be willing to type a sentence and read what comes back.",
  },
  {
    slug: 'what-is-a-prompt',
    title: 'What Is a Prompt? You Already Know How to Write One',
    description:
      "What is a prompt in AI? It's simply the instruction you give an AI tool — no technical skill required. Plain English explanation.",
    date: 'July 2026',
    level: 'L4 Definitions',
    schemaType: 'DefinedTerm',
    subheading: "The word sounds technical. The skill behind it isn't new to you at all.",
  },
  {
    slug: 'how-i-use-ai-to-clear-my-inbox',
    title: 'How I Use AI to Get Through My Inbox in Half the Time',
    description:
      'How to use AI to get through your inbox faster — four real prompts for email summaries, difficult replies, and morning triage. Plain English.',
    date: 'July 2026',
    level: 'L3 Practical',
    schemaType: 'HowTo',
    subheading: 'The exact prompts Anna uses to clear her inbox without losing the morning to it.',
  },
  {
    slug: 'why-does-ai-feel-so-overwhelming',
    title: 'Why Does AI Feel So Overwhelming at First?',
    description:
      "Why does AI feel so overwhelming? It's usually the noise, not the technology. Here's what's actually happening — and how it passes quickly.",
    date: 'June 2026',
    level: 'L2 Questions',
    schemaType: 'FAQPage',
    subheading: "It's not you. Here's what's actually going on — and why it passes.",
  },
  {
    slug: 'am-i-too-old-to-learn-ai',
    title: 'Am I Too Old to Learn AI? Short Answer: No.',
    description:
      "Am I too old to learn AI? No — and here's why that feeling exists and what to do about it. Plain English from ThatClicked.",
    date: 'June 2026',
    level: 'L2 Questions',
    schemaType: 'FAQPage',
    subheading: 'The question almost every professional over 50 asks — and what the answer actually means.',
  },
  {
    slug: 'how-to-start-using-ai-without-feeling-overwhelmed',
    title: 'How to Start Using AI Without Feeling Overwhelmed',
    description:
      'How to start using AI without feeling overwhelmed — a plain English guide for professionals over 50. No jargon. No pressure. Just where to begin.',
    date: 'June 2026',
    level: 'L1 Cornerstone',
    schemaType: 'HowTo',
    subheading: "The practical starting point for professionals who know they should be using AI — and haven't yet.",
  },
  {
    slug: 'ai-in-plain-english-glossary',
    title: "AI in Plain English: A Glossary You'll Actually Use",
    description:
      "AI, LLM, prompting, hallucination — the AI terms professionals hear every day, explained in plain English. No jargon. No hype.",
    date: 'May 2026',
    level: 'L1 Cornerstone',
    schemaType: 'Article',
    subheading: "The words you keep hearing, explained the way a smart colleague would.",
  },
  {
    slug: 'five-things-in-ten-minutes',
    title: '5 Things I Now Do in 10 Minutes That Used to Take Me an Hour',
    description:
      'A month of noticing. Anna kept a rough log of the tasks she used to dread — and what happened when she started doing them differently.',
    date: 'May 2026',
    level: 'L3 Practical',
    schemaType: 'HowTo',
    subheading: "A practical place to start, right now.",
  },
  {
    slug: 'youre-not-behind',
    title: "You're Not Behind. You Just Haven't Started Yet.",
    description:
      "Every AI article makes it sound like you missed something. You didn't. Anna on why experienced professionals are better placed than most — once someone shows them where to start.",
    date: 'April 2026',
    level: 'L5 Anna Notes',
    schemaType: 'FAQPage',
    subheading: "Everyone feels this way at the start. Here's what that actually means.",
  },
  {
    slug: 'the-email-i-couldnt-write',
    title: "The Email I Couldn't Write for Three Days — Until I Asked Copilot",
    description:
      "Some emails just won't come out right. Anna had one sitting in drafts for three days before she finally tried something different.",
    date: 'April 2026',
    level: 'L3 Practical',
    schemaType: 'HowTo',
    subheading: "There was one email I kept putting off. AI helped me write it.",
  },
  {
    slug: 'six-months-nodding-along',
    title: "I Spent Six Months Nodding Along in Meetings About AI. Then I Figured It Out.",
    description:
      "Anna Rippon spent six months bluffing her way through AI conversations at work. Here's what she learned when she finally stopped pretending.",
    date: 'March 2026',
    level: 'L5 Anna Notes',
    schemaType: 'Article',
    subheading: "I smiled and said yes. I had no idea what they were talking about.",
  },
  {
    slug: 'what-ai-actually-replaced',
    title: "I Spent 25 Years in Corporate Life. Here's What AI Actually Replaced — and What It Didn't.",
    description:
      "Experienced professionals aren't being replaced by AI — they're being freed up to do what only they can do. Anna reflects on 25 years in corporate life and what changed when she finally tried it.",
    date: 'March 2026',
    level: 'L5 Anna Notes',
    schemaType: 'Article',
    subheading: "Something shifted. It wasn't dramatic. But it was real.",
  },
];

const levelCards = [
  { label: 'L1 · Cornerstones', description: 'Complete guides and foundational concepts' },
  { label: 'L2 · Questions', description: 'Real concerns and fears — answered honestly' },
  { label: 'L3 · Practical', description: 'Real examples and things to try today' },
  { label: 'L4 · Definitions', description: 'Plain-English explanations of AI terms' },
  { label: 'L5 · Anna Notes', description: 'Reassurance and perspective from Anna' },
  { label: 'L6 · Ask Anna', description: 'Reader questions and practical advice' },
];

const filters = [
  { label: 'All', value: 'All' },
  { label: 'Cornerstones', value: 'L1 Cornerstone' },
  { label: 'Questions', value: 'L2 Questions' },
  { label: 'Practical', value: 'L3 Practical' },
  { label: 'Definitions', value: 'L4 Definitions' },
  { label: 'Anna Notes', value: 'L5 Anna Notes' },
  { label: 'Ask Anna', value: 'L6 Ask Anna' },
];

export default function BlogIndexPage() {
  const [activeFilter, setActiveFilter] = useState('All');

  const filteredPosts =
    activeFilter === 'All'
      ? posts
      : posts.filter((p) => p.level === activeFilter);

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--background)' }}>
      <SiteNav variant="light" currentPath="/blog" />

      <main className="pt-24 pb-24 lg:pt-36 lg:pb-36">
        <div className="max-w-2xl mx-auto px-6 lg:px-0">

          {/* ── INTRO BLOCK ── */}
          <div className="mb-16">
            {/* Eyebrow */}
            <div className="flex items-center gap-2 mb-6">
              <div className="h-px w-8" style={{ backgroundColor: 'var(--amber)' }} aria-hidden="true" />
              <span
                className="font-body text-xs font-semibold uppercase tracking-widest"
                style={{ color: 'var(--amber)', letterSpacing: '0.12em' }}
              >
                That Clicked Blog
              </span>
            </div>

            {/* Headline */}
            <h1
              className="font-display font-semibold mb-4"
              style={{
                fontSize: 'clamp(2rem, 4vw, 2.75rem)',
                color: 'var(--navy)',
                lineHeight: '1.18',
                letterSpacing: '-0.01em',
              }}
            >
              AI confidence in plain English.
            </h1>

            {/* Subheading */}
            <p
              className="font-body mb-10"
              style={{ color: 'var(--text-light)', lineHeight: '1.75', fontSize: '1.05rem', maxWidth: '560px' }}
            >
              Practical help for professionals over 50 who want to understand AI without jargon, pressure, or hype.
            </p>

            {/* Start where you are */}
            <p
              className="font-body text-xs font-semibold uppercase tracking-widest mb-5"
              style={{ color: 'var(--navy)', letterSpacing: '0.12em' }}
            >
              Start where you are:
            </p>

            {/* Level cards grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
              {levelCards.map((card) => (
                <div
                  key={card.label}
                  className="px-5 py-4 rounded"
                  style={{ border: '1px solid var(--border)', backgroundColor: 'transparent' }}
                >
                  <p
                    className="font-body text-sm font-semibold mb-1"
                    style={{ color: 'var(--navy)' }}
                  >
                    {card.label}
                  </p>
                  <p
                    className="font-body text-sm"
                    style={{ color: 'var(--text-light)', lineHeight: '1.6' }}
                  >
                    {card.description}
                  </p>
                </div>
              ))}
            </div>

            {/* Glossary cross-link */}
            <p className="font-body text-sm" style={{ color: 'var(--text-light)' }}>
              Looking for AI term definitions?{' '}
              <Link
                href="/glossary"
                className="underline underline-offset-2 transition-opacity duration-150 hover:opacity-70"
                style={{ color: 'var(--amber)' }}
              >
                Browse the Glossary →
              </Link>
            </p>
          </div>

          {/* ── FILTER ROW ── */}
          <div className="flex flex-wrap gap-x-6 gap-y-2 mb-12" role="group" aria-label="Filter posts by level">
            {filters.map((f) => {
              const isActive = activeFilter === f.value;
              return (
                <button
                  key={f.value}
                  onClick={() => setActiveFilter(f.value)}
                  className="font-body text-sm pb-1 transition-colors duration-150 focus:outline-none"
                  style={{
                    color: isActive ? 'var(--amber)' : 'var(--text-light)',
                    borderBottom: isActive ? '2px solid var(--amber)' : '2px solid transparent',
                    background: 'none',
                    cursor: 'pointer',
                  }}
                  aria-pressed={isActive}
                >
                  {f.label}
                </button>
              );
            })}
          </div>

          {/* ── ARTICLE LIST ── */}
          <div className="flex flex-col gap-12">
            {filteredPosts.map((post) => (
              <article key={post.slug}>
                <Link href={`/blog/${post.slug}`} className="group block">
                  <h2
                    className="font-display font-semibold mb-4 group-hover:text-amber-500 transition-colors duration-150"
                    style={{ fontSize: 'clamp(1.25rem, 2.5vw, 1.6rem)', color: 'var(--navy)', lineHeight: '1.25' }}
                  >
                    {post.title}
                  </h2>
                  <p className="font-body mb-3" style={{ color: 'var(--text-light)', lineHeight: '1.85' }}>
                    {post.description}
                  </p>
                  <p
                    className="font-body mb-4"
                    style={{ color: '#AAAAAA', fontSize: '0.75rem' }}
                  >
                    {post.date}
                  </p>
                  <span
                    className="inline-flex items-center gap-1.5 font-body text-sm font-medium group-hover:gap-2.5 transition-all duration-150"
                    style={{ color: 'var(--amber)' }}
                  >
                    Read: {post.title} <ArrowRight size={14} />
                  </span>
                </Link>
                <div className="h-px w-full mt-12" style={{ backgroundColor: 'var(--border)' }} aria-hidden="true" />
              </article>
            ))}
          </div>

        </div>
      </main>

      <LandingFooter />
    </div>
  );
}
